import type { CSSProperties } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useViewport } from '../hooks/useViewport';
import { ShopProvider, useShop } from './ShopState';
import * as M from './mobile';
import * as D from './desktop';
import { ClosurePage } from './ClosurePage';
import { OrderLookupPage } from './OrderLookupPage';

export function ShopRoutes() {
  const { isMobile } = useViewport();
  const Gate = isMobile ? M.Gate : D.Gate;
  const Main = isMobile ? M.Main : D.Product;
  const Checkout = isMobile ? M.Checkout : D.Checkout;
  const Success = isMobile ? M.Success : D.Success;

  return (
    <ShopProvider>
      <div
        className="poster poster-grain"
        style={isMobile ? mobileFrameStyle : desktopFrameStyle}
      >
        <ShopRouter
          Gate={Gate}
          Main={Main}
          Checkout={Checkout}
          Success={Success}
        />
      </div>
    </ShopProvider>
  );
}

function ShopRouter({
  Gate,
  Main,
  Checkout,
  Success,
}: {
  Gate: () => JSX.Element | null;
  Main: () => JSX.Element | null;
  Checkout: () => JSX.Element | null;
  Success: () => JSX.Element | null;
}) {
  const { catalog, verification } = useShop();
  const shopOpen = catalog.status === 'ready' ? catalog.shopStatus.shop_open : true;
  // Everything past the gate ends in POST /orders, which refuses to create an order without a
  // valid ticket. Letting a visitor reach the product page or checkout without one only buys
  // them a filled-in basket and a rejection at the last step.
  const gated = (page: JSX.Element) => {
    if (!shopOpen) return <ClosurePage />;
    if (!verification) return <Navigate to="/" replace />;
    return page;
  };
  return (
    <Routes>
      <Route index element={shopOpen ? <Gate /> : <ClosurePage />} />
      <Route path="sklep" element={gated(<Main />)} />
      <Route path="sklep/checkout" element={gated(<Checkout />)} />
      <Route path="sklep/sukces/:orderId" element={<Success />} />
      <Route path="zamowienie" element={<OrderLookupPage />} />
      <Route path="zamowienie/:orderId" element={<Success />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const mobileFrameStyle: CSSProperties = {
  width: '100vw',
  minHeight: '100vh',
  margin: 0,
};

// The frame is the desktop scroll root, stated explicitly rather than left to a percentage
// height resolving to auto. Every screen inside already grows past min-height:100vh, and .poster
// hides its overflow, so a frame pinned to the viewport clipped the rest away instead of
// scrolling to it - on a short window the product photo alone floors the layout at ~524px and
// took the size picker and the button with it.
// width is 100% rather than 100vw so a visible scrollbar cannot push the layout sideways.
const desktopFrameStyle: CSSProperties = {
  width: '100%',
  height: '100vh',
  overflowX: 'hidden',
  overflowY: 'auto',
};
